import { useMemo } from 'react';
import { useAccount, useMsal } from '@azure/msal-react';

import { isDemoModeRequested } from '@/lib/demoData';

export interface HubUser {
  name: string;
  email: string;
  /** Display-name set, not GUID set. The token's `groups` claim contains object
   *  IDs; we translate them via the well-known map below so feature code can
   *  read `user.groups.has('hub-elevation-approvers-staging')` without caring
   *  about the GUID layout. */
  groups: ReadonlySet<string>;
}

// Known hub security groups in the corp tenant. Object IDs come from
// `entra-bootstrap.sh` (see its summary output). Membership in any other
// group surfaces in `idTokenClaims.groups` but is intentionally dropped — the
// SPA only cares about these four for now.
//
// Keep these in sync with .pipelines/scripts/entra-bootstrap.sh and the
// backend mirror at src/Hub.Infrastructure/Elevation/HubGroupRegistry.cs. If
// a group is ever re-created (object id changes), updating both maps is the
// only code change needed.
//
// Stored lowercased so the lookup matches regardless of how Entra emits the
// GUID in the token (Entra has historically emitted lowercase, but the spec
// is case-insensitive and Microsoft's own docs warn callers to treat OIDs
// that way — the backend HubGroupRegistry uses an OrdinalIgnoreCase
// Dictionary for the same reason).
const groupNameById: Readonly<Record<string, string>> = Object.freeze({
  'e66f9f1e-0710-45c4-a1c1-bd36d16f1b37': 'hub-elevation-requesters',
  'dd76864b-261e-41ad-9864-b48216018b6d': 'hub-elevation-approvers-staging',
  'd45fe887-78cf-4b66-8335-30edb816865e': 'hub-elevation-approvers-prod',
  '6585284a-2c78-4fbd-8975-3b3653d2394f': 'hub-admins',
});

interface HubIdTokenClaims {
  groups?: readonly string[];
  preferred_username?: string;
  // Overage indicator. Entra emits `_claim_names: { groups: "src1", ... }`
  // (and a sibling `_claim_sources`) when a user is in too many groups to
  // include directly in the token (the limit varies by token type but is
  // ~150-200 for SPAs); resolving the full list requires a Graph call we
  // haven't implemented. The map may contain entries for other claim
  // categories too (e.g. roles), so to detect *groups* overage specifically
  // we only check `_claim_names.groups`.
  _claim_names?: Record<string, string>;
}

/**
 * Returns the signed-in user as the rest of the app expects them. Must be
 * called inside `<AuthProvider>` — the MsalAuthenticationTemplate there gates
 * rendering on a real account, so the throw below is a programmer-error guard,
 * not a normal flow.
 */
export function useHubUser(): HubUser {
  const account = useAccount();
  // isDemoModeRequested() is stable for the lifetime of the app: VITE_DEMO_MODE=1
  // is a build-time constant, and ?demo=1 only applies in dev mode where the URL
  // doesn't change via client-side navigation (AuthProvider sits above BrowserRouter).
  // Hoisting outside useMemo still correctly invalidates on env-var changes during
  // HMR in development.
  const demo = isDemoModeRequested();

  return useMemo<HubUser>(() => {
    if (demo) {
      return { name: 'Demo User', email: 'demo@caredocs.ai', groups: new Set() };
    }

    if (!account) {
      throw new Error(
        'useHubUser called without an active MSAL account. ' +
          'Make sure the consumer is rendered inside <AuthProvider>.',
      );
    }
    const claims = (account.idTokenClaims ?? {}) as HubIdTokenClaims;

    if (claims._claim_names?.groups) {
      console.warn(
        '[auth] groups claim was omitted from the token (Entra overage). ' +
          'The user is in too many groups for Entra to inline; querying Graph ' +
          'for the full list is not implemented yet — UI gating will ' +
          'under-report group membership.',
      );
    }

    const groups = new Set<string>();
    for (const groupId of claims.groups ?? []) {
      // Lowercase the lookup key — see comment on groupNameById for why.
      const name = groupNameById[groupId.toLowerCase()];
      if (name) groups.add(name);
    }

    return {
      name: account.name ?? account.username,
      email: claims.preferred_username ?? account.username,
      groups,
    };
  }, [account, demo]);
}

/** Returns a function that triggers Entra logout via redirect. The token cache
 *  (sessionStorage) is cleared and the user lands back on `/`. */
export function useSignOut(): () => void {
  const { instance } = useMsal();
  return () => {
    if (isDemoModeRequested()) return;
    void instance.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/`,
    });
  };
}
